import classNames from 'classnames';
import * as React from 'react';

import { Button } from 'components/Button';
import Icon from 'components/icon';
import { TempControlCalculationMethod, TempControlProject, TempControlSizingResult } from '__generated-api__';
import CheckboxHighGroup from 'tempcontrol/components/checkbox-high-group';
import { AddOnOptionKey, addOnOptionItems, facilityUsesFlowRate } from 'tempcontrol/enums';
import { formatCalculationMethodLabel } from 'tempcontrol/utils/calculation-method';

const OPTION_FIELD_PREFIX = 'options.';

// Reuse the exact Step-2 add-on style: map each option to the RHF field path
// the CheckboxHighGroup drives (`options.<key>`).
const optionFields = addOnOptionItems.map((item) => ({
  name: `${OPTION_FIELD_PREFIX}${item.key}`,
  label: item.label,
  description: item.description,
}));

interface ResultsAccordionsProps {
  project: TempControlProject;
  result: TempControlSizingResult;
  selectedMethod: TempControlCalculationMethod;
  displayPartNumber: string;
  valveType: string;
  /** Add-ons build the thermostatic part-number suffix, so they only apply to
   *  the Thermostatic (conventional) valve — hidden when Digital is selected. */
  addOnsApply: boolean;
  onToggleOption: (optionKey: AddOnOptionKey, checked: boolean) => void | Promise<void>;
  isUpdatingOptions: boolean;
  onDownload: () => void;
  isDownloading: boolean;
}

const formatNumber = (value?: number | null, fractionDigits = 1) => {
  if (typeof value !== 'number') return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

const toggleAccordion = (
  event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
) => {
  event.preventDefault();
  setOpen((open) => !open);
};

/**
 * s5 accordions — markup and open/close behaviour match tempcontrol-s5.php
 * and the global `accordions.js` pattern (toggle `is-open` on `.js-accordion`,
 * let SCSS control panel visibility). Avoids the shared `useAccordion` hook
 * which runs jQuery slide animations and fights the cutup CSS.
 */
export const ResultsAccordions: React.VFC<ResultsAccordionsProps> = ({
  project,
  result,
  selectedMethod,
  displayPartNumber,
  valveType,
  addOnsApply,
  onToggleOption,
  isUpdatingOptions,
  onDownload,
  isDownloading,
}) => {
  const [methodOpen, setMethodOpen] = React.useState(true);
  const [dataOpen, setDataOpen] = React.useState(false);
  const [optionsOpen, setOptionsOpen] = React.useState(false);

  // The Hunters/Symmons toggle names the calculation *family*, but the
  // actual sizing sub-method depends on the facility: gym/club/industrial/
  // maximum-flow are sized additively (Max Use), not on the Hunter's ASME
  // curve. Reflect that so the note doesn't claim Hunter's for those.
  const usesMaxUseMethod =
    selectedMethod !== TempControlCalculationMethod.Symmons && facilityUsesFlowRate(project.inputs?.facility);
  const methodLabel = usesMaxUseMethod ? 'the Maximum Use method' : formatCalculationMethodLabel(selectedMethod);

  const stats: Array<{ label: string; value: string }> = [
    { label: 'Recommended Part Number(s)', value: displayPartNumber },
    { label: 'Valve Type', value: valveType },
    {
      label: 'Valve Configuration',
      value:
        (result.manifold_count ?? 0) > 0 ? `${(result.manifold_count ?? 0) + 1} valves in parallel` : 'Single valve',
    },
    { label: 'Total Fixture Units', value: formatNumber(result.total_fixture_units) },
    { label: 'Total Flow (GPM)', value: formatNumber(result.total_flow_gpm) },
    { label: 'Actual Pressure Drop', value: `${formatNumber(result.actual_press_drop_psi)} PSI` },
  ];

  if (result.ips_label) {
    stats.push({ label: 'IPS Connections (Inlet x Outlet)', value: result.ips_label });
  }

  return (
    <>
      <div className="c-accordion c-accordion--standard-small has-divider-top">
        <div className={classNames('c-accordion__container js-accordion', { 'is-open': methodOpen })} id="about-method">
          <div
            className="c-accordion-toggle c-accordion-toggle--large js-accordion-toggle"
            role="button"
            tabIndex={0}
            onClick={(event) => toggleAccordion(event, setMethodOpen)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                toggleAccordion(event, setMethodOpen);
              }
            }}
          >
            <p className="c-accordion__title">About Calculation Method</p>
          </div>
          <div className="c-accordion__content js-accordion-container">
            <div className="u-flex u-items-start">
              <div className="u-text-secondary u-mr-spacer-base-small u-flex-shrink-0">
                <Icon name="info" className="o-svg-icon o-svg-large" />
              </div>
              <p className="u-uppercase u-font-medium u-mb-0">Your results are calculated using {methodLabel}.</p>
            </div>
            <p>
              {usesMaxUseMethod
                ? 'For this facility type, Symmons sizes on maximum simultaneous use — peak demand is the sum of each fixture’s quantity × flow rate — then matched against valve pressure-drop coefficients to select the assembly.'
                : 'Hunter’s Method converts the total fixture units to a peak flow rate (GPM), then selects a valve whose flow coefficient (Cv) can deliver that flow at an acceptable pressure drop.'}
            </p>
          </div>
        </div>

        <div className={classNames('c-accordion__container js-accordion', { 'is-open': dataOpen })}>
          <div
            className="c-accordion-toggle c-accordion-toggle--large js-accordion-toggle"
            role="button"
            tabIndex={0}
            onClick={(event) => toggleAccordion(event, setDataOpen)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                toggleAccordion(event, setDataOpen);
              }
            }}
          >
            <p className="c-accordion__title">Show All Data</p>
          </div>
          <div className="c-accordion__content js-accordion-container">
            <p>
              <strong>{project.project_name}</strong>
            </p>
            <div className="o-row c-tile__row c-tile__row--small u-mb-spacer-base-small">
              {stats.map((stat) => (
                <div key={stat.label} className="o-col-12">
                  <article className="c-tile c-tile--compact@xl">
                    <div className="c-tile__body">
                      <div className="c-tile__main c-tile__main--row@xl">
                        <div className="c-tile__label u-flex-grow">
                          <p>{stat.label}</p>
                        </div>
                        <div className="c-tile__title">
                          <h6>{stat.value}</h6>
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </div>

        {addOnsApply && (
          <div className={classNames('c-accordion__container js-accordion', { 'is-open': optionsOpen })}>
            <div
              className="c-accordion-toggle c-accordion-toggle--large js-accordion-toggle"
              role="button"
              tabIndex={0}
              onClick={(event) => toggleAccordion(event, setOptionsOpen)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  toggleAccordion(event, setOptionsOpen);
                }
              }}
            >
              <p className="c-accordion__title">Available Options</p>
            </div>
            <div className="c-accordion__content js-accordion-container">
              <p>
                The following options are available for most TempControl valves; check the valve’s specifications sheet
                for option details.
              </p>
              <CheckboxHighGroup
                items={optionFields}
                disabled={isUpdatingOptions}
                onAfterChange={(name, checked) =>
                  onToggleOption(name.slice(OPTION_FIELD_PREFIX.length) as AddOnOptionKey, checked)
                }
              />
            </div>
          </div>
        )}
      </div>

      <div className="u-flex u-items-center u-flex-wrap u-mt-spacer-base" style={{ gap: '0.75rem' }}>
        <Button variant="primary" onClick={onDownload} isLoading={isDownloading} rightIcon="download">
          Download PDF
        </Button>
        <span className="c-note">All details for this project are saved to download at any time.</span>
      </div>
    </>
  );
};

export default ResultsAccordions;
